import { inject, injectable } from 'inversify';
import { CurrentClientUserDependencyTypes } from '@client/entity/current-client-user/current-client-user.dependency-types';
import type { CurrentClientUserStoreCommand } from '@client/entity/current-client-user/domain/store/current-client-user.store-command';
import type { CurrentClientUserStoreQuery } from '@client/entity/current-client-user/domain/store/current-client-user.store-query';
import type { CurrentClientUserClient } from '@client/entity/current-client-user/domain/model/current-client-user-client.model';
import type { CurrentClientUserSelectionRepositoryInterface } from '@client/entity/current-client-user/domain/repository/current-client-user-selection.repository';
import { CurrentClientUserSelection } from '@client/entity/current-client-user/domain/model/current-client-user-selection.model';

@injectable()
export class CurrentClientUserSelectionService {
  public constructor(
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserStoreQuery)
    private readonly currentClientUserStoreQuery: CurrentClientUserStoreQuery,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserStoreCommand)
    private readonly currentClientUserStoreCommand: CurrentClientUserStoreCommand,
    @inject(CurrentClientUserDependencyTypes.CurrentClientUserSelectionRepository)
    private readonly currentClientUserSelectionRepository: CurrentClientUserSelectionRepositoryInterface,
  ) {}

  public ensureSelectedClient(): void {
    if (this.currentClientUserStoreQuery.getSelectedClient()) {
      return;
    }

    const clients = this.currentClientUserStoreQuery.getDefinedCurrentClientUserClients();
    const storedSelection = this.currentClientUserSelectionRepository.getSelection();
    // A stored selection may belong to a client that is no longer available to the current user.
    const storedClient = clients.find(({ id }) => id === storedSelection?.clientId);
    if (storedSelection && !storedClient) {
      this.currentClientUserSelectionRepository.clearSelection();
    }

    const client = storedClient ?? clients[0];
    if (client) {
      this.selectClient(client);
    }
  }

  public selectClient(client: CurrentClientUserClient): void {
    this.currentClientUserStoreCommand.setSelectedClient(client);
    this.currentClientUserSelectionRepository.saveSelection(new CurrentClientUserSelection(client.id));
  }
}
